import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUploadContext, presignUploads, postToPresigned, completeUpload } from '../api/jobs';
import type { UploadContext } from '../types';
import { Spinner, ErrorBanner, toMessage } from '../components/common';
import { PublicBrand } from '../components/PublicBrand';

interface Tracked {
  file: File;
  progress: number; // 0..1
  status: 'ready' | 'uploading' | 'done' | 'error';
  error?: string;
  requirement: string; // which required document this file answers ('' = not chosen yet)
}

// Escape-hatch option so a client with an extra document is never stuck.
const OTHER_REQUIREMENT = 'Other / not listed';

// Client-side gate, mirrored by the server (upload-presign) which is authoritative.
const ALLOWED_EXT = new Set(['xlsx', 'xlsm', 'xls', 'docx', 'pdf', 'csv']);
const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB per file

// PUBLIC client-facing upload portal. No sign-in. The token in the URL is the
// only credential; CloudFront supplies the origin-verify header for /api calls.
export function Upload({
  embedded = false,
  onOutstandingChange,
}: { embedded?: boolean; onOutstandingChange?: (n: number) => void } = {}) {
  // `embedded` is the Collect information portal rendering this as one of two tabs.
  // It owns the brand header and the progress stepper in that mode, so this must not
  // draw a second set.
  const { token = '' } = useParams();
  const [ctx, setCtx] = useState<UploadContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [files, setFiles] = useState<Tracked[]>([]);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allDone, setAllDone] = useState(false);
  // The client saying "I have finished", which is not the same as having uploaded.
  // No name is asked for: they typed one on the questionnaire, which always comes
  // first, and asking the same person twice on one link is pure friction.
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getUploadContext(token)
      .then((res) => {
        if (!cancelled) setCtx(res);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(toMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    if (list.length === 0) return;
    const extOf = (n: string) => {
      const d = n.lastIndexOf('.');
      return d >= 0 ? n.slice(d + 1).toLowerCase() : '';
    };
    const valid: File[] = [];
    const rejected: string[] = [];
    for (const file of list) {
      if (!ALLOWED_EXT.has(extOf(file.name))) {
        rejected.push(`${file.name} (unsupported type)`);
      } else if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name} (over 25 MB)`);
      } else {
        valid.push(file);
      }
    }
    setError(rejected.length ? `Not added: ${rejected.join(', ')}.` : null);
    if (valid.length === 0) return;
    setFiles((prev) => [
      ...prev,
      ...valid.map((file) => ({ file, progress: 0, status: 'ready' as const, requirement: '' })),
    ]);
    setAllDone(false);
  }, []);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  }

  function removeAt(i: number) {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  }

  function setTracked(i: number, patch: Partial<Tracked>) {
    setFiles((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }

  async function upload() {
    const pending = files
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => t.status === 'ready' || t.status === 'error');
    if (pending.length === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const targets = await presignUploads(
        token,
        pending.map(({ t }) => ({
          filename: t.file.name,
          contentType: t.file.type || 'application/octet-stream',
          requirement: t.requirement,
        })),
      );

      for (const { t, i } of pending) {
        const target = targets.find((x) => x.filename === t.file.name);
        if (!target) {
          setTracked(i, { status: 'error', error: 'No upload URL was issued for this file.' });
          continue;
        }
        setTracked(i, { status: 'uploading', progress: 0, error: undefined });
        try {
          await postToPresigned(target, t.file, (fraction) =>
            setTracked(i, { progress: fraction }),
          );
          setTracked(i, { status: 'done', progress: 1 });
        } catch (err) {
          setTracked(i, { status: 'error', error: toMessage(err) });
        }
      }

      setFiles((prev) => {
        const done = prev.length > 0 && prev.every((t) => t.status === 'done');
        setAllDone(done);
        return prev;
      });
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Client journey stepper. Until job status is exposed on the upload context
  // (calibration build), the active step derives from this page's own state:
  // arriving here means the engagement stands (step 1 done) and documents are
  // due (step 2 active); a completed upload moves the journey to our review.
  const statusStep: Record<string, number> = {
    DRAFT: 1, QUOTE_SENT: 1,
    PROPOSAL_SIGNED: 2, AWAITING_DOCS: 2,
    DOCS_RECEIVED: 3, REVIEW: 3, QUERIES_SENT: 3,
    // legacy statuses on old jobs
    QUOTE_DRAFTED: 1, QUERIES_DRAFTED: 3, CLOSED: 4,
  };
  const fromStatus = ctx?.status ? statusStep[ctx.status] : undefined;
  const activeStep = allDone ? 3 : Math.max(fromStatus ?? 2, 2);
  // What has actually been supplied: what the server already had when the page
  // loaded, plus anything uploaded since, so the ticks appear without a reload.
  const suppliedReqs = new Set<string>([
    ...(ctx?.requirements ?? []).filter((r) => r.supplied).map((r) => r.label.trim().toLowerCase()),
    ...files
      .filter((t) => t.status === 'done' && t.requirement)
      .map((t) => t.requirement.trim().toLowerCase()),
  ]);
  const outstandingReqs = (ctx?.docRequirements ?? []).filter(
    (r) => r.trim() && !suppliedReqs.has(r.trim().toLowerCase()),
  );
  // The tab chip above is rendered by the parent from the context it loaded on page
  // open, so it kept saying "2 still to send" while both rows below were green.
  useEffect(() => {
    onOutstandingChange?.(outstandingReqs.length);
  }, [outstandingReqs.length, onOutstandingChange]);

  // Submitted in this session, or already submitted when the page loaded, so a client
  // coming back to the link sees the review state rather than a fresh form.
  const submittedAt = finished || Boolean(ctx?.docsSubmittedAt);

  async function finish() {
    setFinishError(null);
    if (outstandingReqs.length) return; // the button is disabled; belt and braces
    setFinishing(true);
    try {
      await completeUpload(token);
      setFinished(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setFinishError(toMessage(err));
    } finally {
      setFinishing(false);
    }
  }

  const steps = [
    { n: 1, label: 'Engagement', sub: 'Quote accepted' },
    { n: 2, label: 'Your documents', sub: 'Provide your files' },
    { n: 3, label: 'Our review', sub: 'We may come back with queries' },
    { n: 4, label: 'Valuation report', sub: 'Prepared and delivered' },
  ];
  const stepper = (
    <div className="stepper" aria-label="Progress">
      {steps.map((s) => (
        <div
          key={s.n}
          className={`step${s.n < activeStep ? ' done' : ''}${s.n === activeStep ? ' active' : ''}${s.n === 4 ? ' final' : ''}`}
        >
          <span className="dot">{s.n < activeStep ? '✓' : s.n}</span>
          <div className="step-label">{s.label}</div>
          <div className="step-sub">{s.sub}</div>
        </div>
      ))}
    </div>
  );

  if (loadError) {
    return (
      <div className="public-wrap">
        <div className="card public-card">
          <PublicBrand />
          <ErrorBanner message={loadError} />
          <p className="muted">
            This upload link may have expired or already been used. Please contact your WA Business
            Valuations adviser for a new link.
          </p>
        </div>
      </div>
    );
  }
  if (!ctx) return <Spinner label="Loading." />;

  return (
    <div className={embedded ? undefined : 'public-wrap'}>
      {embedded ? null : (
        <>
          <div className="card public-card" style={{ textAlign: 'center' }}>
            <PublicBrand />
          </div>
          <div className="card">{stepper}</div>
        </>
      )}
      <div className="card">
        {embedded ? null : <h1>Document upload</h1>}
        <p className="muted">
          For {ctx.entityName}. Please provide the following documents.
        </p>
        <div className="secure-note">
          <span className="secure-note-icon" aria-hidden="true">🔒</span>
          <span>
            This upload is more secure than email. Your files travel over an encrypted connection
            straight to WA Business Valuations, and are never sent as email attachments. When you
            submit, we record the date and time, your IP address and your browser, as a record of
            who provided these documents.
          </span>
        </div>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Accepted formats: Excel (.xls, .xlsx), Word (.docx), PDF and CSV. Up to 25 MB per file.
        </p>
        {ctx.docRequirements && ctx.docRequirements.length > 0 ? (
          <ul className="req-list">
            {ctx.docRequirements.map((r, i) => {
              const done = suppliedReqs.has(r.trim().toLowerCase());
              const missing = !done;
              return (
                <li key={i} className={`${done ? 'done' : ''}${missing ? ' missing' : ''}`}>
                  <span className="req-mark" aria-hidden="true">{done ? '✓' : missing ? '!' : ''}</span>
                  {r}
                  {missing ? <span className="req-note">still needed</span> : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      {allDone && !finished ? (
        <div className="card">
          <h2>Files received</h2>
          <p className="muted">
            Thank you. Add anything else below, then tell us you have finished at the bottom of
            the page.
          </p>
        </div>
      ) : null}

      <div className="card">
        <label
          className={`dropzone ${dragging ? 'drag' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          style={{ display: 'block', cursor: 'pointer' }}
        >
          <input
            type="file"
            multiple
            accept=".xls,.xlsx,.xlsm,.docx,.pdf,.csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <strong>Drag files here</strong>
          <div>or click to choose</div>
        </label>

        {files.length > 0 ? (
          <div style={{ marginTop: '1rem' }}>
            {files.map((t, i) => (
              <div key={i} className="file-row">
                <span style={{ minWidth: 0, flex: '0 1 30%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.file.name}
                </span>
                {t.status === 'ready' || t.status === 'error' ? (
                  <select
                    className={`req-select${t.requirement ? '' : ' unset'}`}
                    value={t.requirement}
                    onChange={(e) => setTracked(i, { requirement: e.target.value })}
                    disabled={submitting}
                    style={{ flex: '0 1 30%', minWidth: 0 }}
                    aria-label="Which document is this?"
                  >
                    <option value="" disabled>Which document is this?</option>
                    {(ctx.docRequirements ?? []).map((r, ri) => (
                      <option key={ri} value={r}>{r}</option>
                    ))}
                    <option value={OTHER_REQUIREMENT}>{OTHER_REQUIREMENT}</option>
                  </select>
                ) : (
                  <span className="muted" style={{ flex: '0 1 30%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.requirement || '—'}
                  </span>
                )}
                {t.status === 'error' ? (
                  <span style={{ color: 'var(--err)', flex: 1 }}>{t.error}</span>
                ) : (
                  <span className="progress">
                    <span style={{ width: `${Math.round(t.progress * 100)}%` }} />
                  </span>
                )}
                <span className="muted" style={{ minWidth: 64, textAlign: 'right' }}>
                  {t.status === 'done'
                    ? 'Done'
                    : t.status === 'uploading'
                      ? `${Math.round(t.progress * 100)}%`
                      : t.status === 'error'
                        ? 'Failed'
                        : 'Ready'}
                </span>
                {t.status === 'ready' ? (
                  <button className="btn-secondary" onClick={() => removeAt(i)} disabled={submitting}>
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {files.some((t) => (t.status === 'ready' || t.status === 'error') && !t.requirement) ? (
          <p className="muted" style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
            For each file, please tell us which document it is. This lets us check your documents
            faster and tell you precisely if anything is missing.
          </p>
        ) : null}
        <div className="btn-row" style={{ marginTop: '1rem' }}>
          <button
            onClick={upload}
            disabled={
              submitting ||
              files.every((t) => t.status === 'done') ||
              files.some((t) => (t.status === 'ready' || t.status === 'error') && !t.requirement)
            }
          >
            {submitting ? 'Uploading.' : 'Upload files'}
          </button>
          {/*
            "I have sent everything, please review it." Disabled until every requested
            category has a file against it, with the reason stated underneath rather
            than left as a dead button.
          */}
          {/*
            Green once it is live, matching the tick on the completed step. Until then
            it is a quiet outline button: an enabled action and a blocked one must not
            look the same, and the client has to be able to tell at a glance that the
            ball is now in their court.
          */}
          <button
            className={outstandingReqs.length ? 'btn-secondary' : 'btn-go'}
            onClick={finish}
            disabled={finishing || outstandingReqs.length > 0}
            title={
              outstandingReqs.length
                ? `Still to send: ${outstandingReqs.join(', ')}`
                : 'Tell us you have finished so we can start the review'
            }
          >
            {finishing ? 'Sending.' : 'Submit'}
          </button>
        </div>
        {finishError ? <ErrorBanner message={finishError} /> : null}
        {outstandingReqs.length ? (
          <p className="field-hint">
            Submit unlocks once we have {outstandingReqs.join(' and ')}.
          </p>
        ) : (
          <p className="field-hint">
            Press Submit when you have sent everything, and we will start the review.
          </p>
        )}
      </div>

      {submittedAt ? (
        <div className="card">
          <h2>Thank you, this is with us now</h2>
          <p className="muted">
            We have everything we asked for and have started the review. We will come back
            to you if anything needs clarifying. If you spot something you sent in error,
            you can still add a corrected file above.
          </p>
        </div>
      ) : null}
    </div>
  );
}
